import * as React from 'react';
import { FileUp, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { FileAttachmentRow } from '@ooh/ui/components/file-attachment-row';
import type { Attachment } from '@ooh/contracts';
import {
  ATTACHMENT_MAX_SIZE_BYTES,
  ATTACHMENT_ALLOWED_EXTENSIONS,
} from './lib/attachment-constants';
import { useUploadAttachment } from './api/mutations';
import type { UploadAttachmentError } from './api/mutations';

export interface UploadZoneLabels {
  uploading: string;
  errorSize: string;
  errorSizeHint: string;
  errorType: string;
  errorTypeHint: string;
  dragOver: string;
  uploadZoneHint: string;
  uploadZoneFormats: string;
  uploadButton: string;
}

interface UploadZoneContentProps {
  isDragOver: boolean;
  isUploading: boolean;
  showSizeError: boolean;
  showTypeError: boolean;
  showServerError: boolean;
  serverErrorMessage: string;
  onButtonClick: () => void;
  labels: UploadZoneLabels;
}

export function UploadZoneContent({
  isDragOver,
  isUploading,
  showSizeError,
  showTypeError,
  showServerError,
  serverErrorMessage,
  onButtonClick,
  labels,
}: UploadZoneContentProps) {
  if (isUploading) {
    return (
      <div className="flex flex-col items-center gap-2">
        <Loader2 className="h-6 w-6 text-primary animate-spin" />
        <p className="text-[13px] text-muted-foreground">{labels.uploading}</p>
      </div>
    );
  }
  if (showSizeError) {
    return (
      <div className="flex flex-col items-center gap-2" data-testid="upload-error-size">
        <AlertCircle className="h-6 w-6 text-destructive" />
        <p className="text-[13px] font-medium text-destructive">{labels.errorSize}</p>
        <p className="text-xs text-muted-foreground">{labels.errorSizeHint}</p>
      </div>
    );
  }
  if (showTypeError) {
    return (
      <div className="flex flex-col items-center gap-2" data-testid="upload-error-type">
        <AlertCircle className="h-6 w-6 text-destructive" />
        <p className="text-[13px] font-medium text-destructive">{labels.errorType}</p>
        <p className="text-xs text-muted-foreground">{labels.errorTypeHint}</p>
      </div>
    );
  }
  if (showServerError) {
    return (
      <div className="flex flex-col items-center gap-2" data-testid="upload-error-server">
        <AlertCircle className="h-6 w-6 text-destructive" />
        <p className="text-[13px] font-medium text-destructive">{serverErrorMessage}</p>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-2">
      <FileUp
        className={`h-6 w-6 transition-colors ${isDragOver ? 'text-primary' : 'text-muted-foreground'}`}
      />
      <p className="text-[13px] text-muted-foreground">
        {isDragOver ? labels.dragOver : labels.uploadZoneHint}
      </p>
      <p className="text-2xs text-muted-foreground/70">{labels.uploadZoneFormats}</p>
      <Button variant="outline" size="sm" data-testid="upload-button" onClick={onButtonClick}>
        <FileUp className="h-3.5 w-3.5" />
        {labels.uploadButton}
      </Button>
    </div>
  );
}

interface AttachmentListProps {
  attachments: Attachment[];
  isUploading: boolean;
  emptyLabel: string;
  onDelete?: (id: number) => void;
}

export function AttachmentList({
  attachments,
  isUploading,
  emptyLabel,
  onDelete,
}: AttachmentListProps) {
  if (attachments.length === 0) {
    return (
      <p
        className="text-[13px] text-muted-foreground italic text-center py-1"
        data-testid="spec-files-empty"
      >
        {emptyLabel}
      </p>
    );
  }
  return (
    <div className="space-y-1.5" data-testid="spec-files-list">
      {attachments.map((att) => (
        <FileAttachmentRow
          key={att.id}
          fileId={att.id}
          filename={att.filename}
          fileType={att.fileType}
          sizeBytes={att.sizeBytes}
          uploadedAt={att.uploadedAt}
          onDownload={() => {
            // eslint-disable-next-line i18next/no-literal-string -- browser target attr, not user-facing text
            window.open(att.downloadUrl, '_blank');
          }}
          onDelete={onDelete ? () => onDelete(att.id) : undefined}
          disabled={isUploading}
        />
      ))}
    </div>
  );
}

export function useUploadState(mediaUnitId: string | number, errorLabel: string) {
  const [clientSizeError, setClientSizeError] = React.useState(false);
  const [clientTypeError, setClientTypeError] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const uploadMutation = useUploadAttachment();

  const uploadError = uploadMutation.error as UploadAttachmentError | null;
  const showSizeError = clientSizeError;
  const showTypeError = clientTypeError;
  const showServerError = serverError !== null || uploadError?.type === 'server';
  const serverErrorMessage = serverError ?? uploadError?.message ?? errorLabel;

  function handleFile(file: File) {
    setClientSizeError(false);
    setClientTypeError(false);
    setServerError(null);
    uploadMutation.reset();
    if (file.size > ATTACHMENT_MAX_SIZE_BYTES) {
      setClientSizeError(true);
      return;
    }
    const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    if (
      !ATTACHMENT_ALLOWED_EXTENSIONS.includes(ext as (typeof ATTACHMENT_ALLOWED_EXTENSIONS)[number])
    ) {
      setClientTypeError(true);
      return;
    }
    uploadMutation.mutate(
      { mediaUnitId, file },
      {
        onError: (err) => {
          if (err.type === 'server') setServerError(err.message ?? errorLabel);
        },
      },
    );
  }

  return {
    isUploading: uploadMutation.isPending,
    showSizeError,
    showTypeError,
    showServerError,
    serverErrorMessage,
    handleFile,
  };
}
